import { useState } from 'react'
import { useSelector } from 'react-redux'
import { ScoopText, ScoopAccordion, ScoopSpinner, ScoopTable } from '../../../../../../ui'
import { useProbeData } from '../../../../../../../api/hooks/domainIntelligence/useProbeData'
import styles from './ProbeTable.module.scss'

const UUID_PATTERN = /[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}/gi
const LONG_HASH_PATTERN = /(?:^|_)[a-f0-9]{8,}(?=_|$)/gi

const humanizeText = (value) => {
    if (!value) return ''

    return value
        .toString()
        .replace(UUID_PATTERN, '')
        .replace(LONG_HASH_PATTERN, '')
        .replace(/^probe_/, '')
        .replace(/^inv_?/, 'investigation ')
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase())
}

const getProbeTitle = (probe) => {
    const configuredTitle = probe.displayName || probe.name || probe.title
    if (configuredTitle) return configuredTitle

    const idTitle = humanizeText(probe.probeId)
    const contextTitle = humanizeText(probe.contextName || probe.findingType)
    if (idTitle && /^\d+$/.test(idTitle)) return `Probe ${idTitle}`
    if (idTitle && !['probe', 'investigation'].includes(idTitle.toLowerCase())) return idTitle
    if (contextTitle) return `${contextTitle} Evidence`

    return 'Evidence Probe'
}

const getProbeContext = (probe) =>
    [humanizeText(probe.contextName), humanizeText(probe.findingType)].filter(Boolean).join(' · ')

export const ProbeTable = ({ probe, runPath }) => {
    const workspaceID = useSelector((state) => state.auth.workspaceID)
    const [expanded, setExpanded] = useState(false)

    const { probeData, loadingProbeData, probeDataError } = useProbeData(
        workspaceID,
        runPath,
        expanded ? probe.probeId : null,
        // Entity-scoped fetch (audit F1): probe ids repeat across entities, so
        // the entity id from the probe index must travel with the request.
        probe.entityId || null,
        // Pass-scoped fetch (audit F1b): the same probe can run in multiple
        // passes within an entity; the index row's ordinal addresses the exact
        // record instead of always the first pass.
        probe.probeOrdinal
    )

    const handleChange = (_event, isExpanded) => {
        setExpanded(isExpanded)
    }

    const probeTitle = getProbeTitle(probe)
    const probeContext = getProbeContext(probe)

    // Build table columns and rows from probe data
    const buildTableData = () => {
        if (!probeData) return { columns: [], rows: [] }

        const parsed = typeof probeData === 'string' ? JSON.parse(probeData) : probeData

        // Get columns from queryResult or from probe index — handle string[] or object[]
        const rawColumns = parsed?.queryResult?.columns || probe.columns || []
        const columns = rawColumns.map((col, idx) => ({
            field: typeof col === 'string' ? col : col.columnName || col.name || `col_${idx}`,
            header:
                typeof col === 'string' ? col : col.columnName || col.name || `Column ${idx + 1}`,
            flex: 1,
            minWidth: 100,
        }))

        // Get rows: prefer dataEvidence.topRows, then dataEvidence.rows, then queryResult.rows
        const rawRows =
            parsed?.dataEvidence?.topRows ||
            parsed?.dataEvidence?.rows ||
            parsed?.queryResult?.rows ||
            []

        // Handle both array-of-arrays and array-of-objects
        const rows = rawRows.map((row, rowIdx) => {
            if (Array.isArray(row)) {
                const obj = { _rowId: rowIdx }
                columns.forEach((col, colIdx) => {
                    obj[col.field] = row[colIdx] ?? ''
                })
                return obj
            }
            return { _rowId: rowIdx, ...row }
        })

        return { columns, rows }
    }

    const { columns, rows } = expanded ? buildTableData() : { columns: [], rows: [] }

    return (
        <ScoopAccordion onChange={handleChange}>
            <ScoopAccordion.Summary>
                <div className={styles.probeSummary}>
                    <div className={styles.probeIdentity}>
                        <ScoopText className={styles.probeTitle}>{probeTitle}</ScoopText>
                        {probeContext && (
                            <ScoopText className={styles.probeContext}>{probeContext}</ScoopText>
                        )}
                    </div>
                    <div className={styles.probeMeta}>
                        {probe.rowCount != null && (
                            <ScoopText className={styles.probeRowCount}>
                                {probe.rowCount} rows
                            </ScoopText>
                        )}
                    </div>
                </div>
            </ScoopAccordion.Summary>
            <ScoopAccordion.Details>
                <div className={styles.probeContent}>
                    {probe.naturalLanguageQuery && (
                        <div className={styles.queryBlock}>
                            <ScoopText className={styles.queryLabel}>Query</ScoopText>
                            <ScoopText className={styles.queryText}>
                                {probe.naturalLanguageQuery}
                            </ScoopText>
                        </div>
                    )}
                    {loadingProbeData && (
                        <div className={styles.probeLoading}>
                            <ScoopSpinner size={20} />
                        </div>
                    )}
                    {probeDataError && (
                        <ScoopText className={styles.probeError}>
                            Failed to load probe data: {probeDataError.message}
                        </ScoopText>
                    )}
                    {!loadingProbeData && !probeDataError && columns.length > 0 && (
                        <ScoopTable
                            columns={columns}
                            rows={rows}
                            sorting={{ enabled: true }}
                            pagination={{ enabled: true, pageSize: 10 }}
                            domLayout="autoHeight"
                            rowHeight={32}
                            headerHeight={40}
                        />
                    )}
                    {!loadingProbeData && !probeDataError && columns.length === 0 && probeData && (
                        <ScoopText className={styles.probeEmpty}>
                            No tabular data available
                        </ScoopText>
                    )}
                </div>
            </ScoopAccordion.Details>
        </ScoopAccordion>
    )
}
